import { Box } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import {
  DatePicker,
  DatePickerProps,
  DateRange,
  DateRangePicker,
} from '@mui/x-date-pickers-pro';
import { Moment } from 'moment/moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSxStyles } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportDateRangeSelector/useStyles';
import {
  convertToDateOrUndefined,
  convertToMomentOrNull,
  convertToMomentOrUndefined,
  isSameDay,
} from '~/utils/helpers/date';

export enum ReportDateRangeSelectorMode {
  DateRange = 'DATE_RANGE',
  YearRange = 'YEAR_RANGE',
  SingleDateRange = 'SINGLE_DATE_RANGE',
}

type PropsType = {
  startDate?: Date;
  endDate?: Date;
  onRangeChange: (startDate?: Date, endDate?: Date) => void;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  mode: ReportDateRangeSelectorMode;
  size?: TextFieldProps['size'];
};

export const ReportDateRangeSelector = (props: PropsType) => {
  const {
    startDate,
    endDate,
    onRangeChange,
    mode,
    required,
    minDate,
    maxDate,
    size,
  } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const dateValues = useMemo(() => {
    return {
      startValue: convertToMomentOrNull(startDate),
      endValue: convertToMomentOrNull(endDate),
      minValue: convertToMomentOrUndefined(minDate),
      maxValue: convertToMomentOrUndefined(maxDate),
    };
  }, [startDate, endDate, minDate, maxDate]);

  const hasDateChanged = (date1: Date | undefined, date2: Date | undefined) => {
    return !!date1 && !!date2 ? !isSameDay(date1, date2) : date1 !== date2;
  };

  const handleRangeChange = useCallback(
    (dateRange: DateRange<Moment>) => {
      const [start, end] = dateRange;
      const newStart = convertToDateOrUndefined(start);
      const newEnd = convertToDateOrUndefined(end);
      if (
        hasDateChanged(newStart, startDate) ||
        hasDateChanged(newEnd, endDate)
      ) {
        onRangeChange(newStart, newEnd);
      }
    },
    [onRangeChange, startDate, endDate],
  );

  switch (mode) {
    case ReportDateRangeSelectorMode.DateRange:
      return (
        <DateRangePicker
          value={[dateValues.startValue, dateValues.endValue]}
          onChange={handleRangeChange}
          minDate={dateValues.minValue}
          maxDate={dateValues.maxValue}
          label={`${t('startDate')} - ${t('endDate')}`}
          slotProps={{
            textField: {
              size: size ?? 'small',
              required,
            },
          }}
          sx={{ minWidth: 260 }}
        />
      );
    case ReportDateRangeSelectorMode.YearRange: {
      const commonProps: Partial<DatePickerProps> = {
        views: ['year'],
        format: 'YYYY',
        slotProps: {
          textField: {
            required,
            size: size ?? 'small',
            sx: sxStyles.yearField,
          },
        },
      };

      return (
        <Box sx={sxStyles.yearRange}>
          <DatePicker
            value={dateValues.startValue}
            onChange={(value) =>
              handleRangeChange([value, dateValues.endValue])
            }
            label={t('startDate')}
            minDate={dateValues.minValue}
            maxDate={
              convertToMomentOrUndefined(dateValues.endValue) ??
              dateValues.maxValue
            }
            {...commonProps}
          />
          <Box sx={sxStyles.rangeDivider}>-</Box>
          <DatePicker
            value={dateValues.endValue}
            onChange={(value) =>
              handleRangeChange([dateValues.startValue, value])
            }
            label={t('endDate')}
            minDate={
              convertToMomentOrUndefined(dateValues.startValue) ??
              dateValues.minValue
            }
            maxDate={dateValues.maxValue}
            {...commonProps}
          />
        </Box>
      );
    }
    case ReportDateRangeSelectorMode.SingleDateRange:
      return (
        <DatePicker
          value={dateValues.endValue}
          onChange={(value) => handleRangeChange([value, value])}
          label={t('date')}
          minDate={dateValues.minValue}
          maxDate={dateValues.maxValue}
          slotProps={{
            textField: {
              required,
              size: size ?? 'small',
            },
          }}
        />
      );
    default:
      return <></>;
  }
};
